import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { DialogDescription } from '@radix-ui/react-dialog';

import { useDialog, useDialogContext } from '@components/primitives/Dialog';
import {
  DialogConfirmation,
  DialogConfirmationProps,
} from '@components/primitives/Dialog/DialogConfirmation';

import css from './Dialog.module.css';

export interface DialogLockProps extends PropsWithChildren {
  title?: DialogConfirmationProps['title'];
  text?: ReactNode;
  label?: DialogConfirmationProps['label'];
  variant?: DialogConfirmationProps['variant'];
}

export function DialogLock({
  title = 'Unsaved changes',
  text = `Do you want to discard your changes? They will be lost.`,
  label = 'Discard',
  variant = 'danger',
  children,
}: DialogLockProps) {
  const state = useDialog();
  const confirmedRef = useRef(false);

  const parentState = useDialogContext();
  useEffect(() => {
    confirmedRef.current = false;
    const unsubscribe = parentState.subscribe((event) => {
      if (!event.detail.open && !confirmedRef.current) {
        event.preventDefault();
        state.open();
      }
    });
    return () => unsubscribe();
  }, [parentState.subscribe]);

  const confirm = useCallback(() => {
    confirmedRef.current = true;
    parentState.close();
  }, [parentState.close]);

  return (
    <DialogConfirmation
      state={state}
      title={title}
      label={label}
      variant={variant}
      onConfirm={confirm}
    >
      <div className={css.lockContent}>
        <DialogDescription>{text}</DialogDescription>
        {children}
      </div>
    </DialogConfirmation>
  );
}
